import React, { useState, useEffect, useCallback } from "react";
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    query,
    orderBy,
    onSnapshot,
    Timestamp,
    where
} from "firebase/firestore";
import { db, auth } from "../firebase";
import Loader from "../components/Loader";

const CATEGORIES = ["General", "Staff", "Customer", "Accounts", "Service", "Important"];
const PRIORITIES = ["low", "medium", "high"];

export default function AdminNotes() {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterCategory, setFilterCategory] = useState("All");
    const [filterPriority, setFilterPriority] = useState("All");
    const [showArchived, setShowArchived] = useState(false);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        id: null,
        title: "",
        content: "",
        category: "General",
        priority: "low",
        pinned: false
    });

    useEffect(() => {
        const q = query(collection(db, "adminNotes"), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const notesData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setNotes(notesData);
            setLoading(false);
        });

        // Keyboard Shortcut: Ctrl + N
        const handleKeyDown = (e) => {
            if (e.ctrlKey && e.key === 'n') {
                e.preventDefault();
                openAddModal();
            }
        };
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            unsubscribe();
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const openAddModal = () => {
        setForm({ id: null, title: "", content: "", category: "General", priority: "low", pinned: false });
        setShowModal(true);
    };

    const openEditModal = (note) => {
        setForm({ ...note });
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return alert("Title is required");

        const data = {
            title: form.title,
            content: form.content,
            category: form.category,
            priority: form.priority,
            pinned: form.pinned,
            archived: false,
            updatedAt: Timestamp.now(),
            createdBy: auth.currentUser?.email || "Admin"
        };

        try {
            setSaving(true);
            if (form.id) {
                await updateDoc(doc(db, "adminNotes", form.id), data);
            } else {
                await addDoc(collection(db, "adminNotes"), {
                    ...data,
                    createdAt: Timestamp.now()
                });
            }
            setShowModal(false);
        } catch (err) {
            console.error("Error saving note:", err);
            alert("Failed to save note");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this note permanently?")) return;
        try {
            await deleteDoc(doc(db, "adminNotes", id));
        } catch (err) {
            console.error("Error deleting note:", err);
        }
    };

    const togglePin = async (note) => {
        try {
            await updateDoc(doc(db, "adminNotes", note.id), { pinned: !note.pinned });
        } catch (err) {
            console.error("Error toggling pin:", err);
        }
    };

    const toggleArchive = async (note) => {
        try {
            await updateDoc(doc(db, "adminNotes", note.id), { archived: !note.archived });
        } catch (err) {
            console.error("Error toggling archive:", err);
        }
    };

    const filteredNotes = notes.filter(n => {
        const matchesSearch = n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            n.content.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === "All" || n.category === filterCategory;
        const matchesPriority = filterPriority === "All" || n.priority === filterPriority;
        const matchesArchive = n.archived === showArchived;

        return matchesSearch && matchesCategory && matchesPriority && matchesArchive;
    });

    const pinnedNotes = filteredNotes.filter(n => n.pinned);
    const regularNotes = filteredNotes.filter(n => !n.pinned);

    return (
        <div className="container-fluid mt-4 px-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="fw-bold">Admin Notes</h3>
                <button className="btn btn-primary" onClick={openAddModal}>
                    <i className="bi bi-plus-lg me-1"></i> + New Note (Ctrl+N)
                </button>
            </div>

            {/* Filters Bar */}
            <div className="row g-2 mb-4 bg-light p-3 rounded shadow-sm">
                <div className="col-md-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search notes..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="col-md-3">
                    <select className="form-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                        <option value="All">All Categories</option>
                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="col-md-3">
                    <select className="form-select" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
                        <option value="All">All Priorities</option>
                        {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>
                </div>
                <div className="col-md-2 d-flex align-items-center">
                    <div className="form-check form-switch">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="archiveSwitch"
                            checked={showArchived}
                            onChange={() => setShowArchived(!showArchived)}
                        />
                        <label className="form-check-label small ms-1" htmlFor="archiveSwitch">
                            Archived
                        </label>
                    </div>
                </div>
            </div>

            {loading ? (
                <Loader />
            ) : (
                <>
                    {pinnedNotes.length > 0 && (
                        <div className="mb-5">
                            <h6 className="text-muted text-uppercase small ls-wide mb-3">Pinned</h6>
                            <div className="row g-3">
                                {pinnedNotes.map(n => <NoteCard key={n.id} note={n} onEdit={openEditModal} onPin={togglePin} onDelete={handleDelete} onArchive={toggleArchive} />)}
                            </div>
                        </div>
                    )}

                    <div>
                        {pinnedNotes.length > 0 && <h6 className="text-muted text-uppercase small ls-wide mb-3">Others</h6>}
                        <div className="row g-3">
                            {regularNotes.map(n => <NoteCard key={n.id} note={n} onEdit={openEditModal} onPin={togglePin} onDelete={handleDelete} onArchive={toggleArchive} />)}
                        </div>
                    </div>

                    {filteredNotes.length === 0 && (
                        <div className="text-center py-5">
                            <h5 className="text-muted">No notes found.</h5>
                        </div>
                    )}
                </>
            )}

            {/* Note Modal */}
            {showModal && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden">
                            <form onSubmit={handleSave}>
                                <div className="modal-header border-0 pb-0 px-4 pt-4">
                                    <h5 className="modal-title fw-bold text-primary">
                                        {form.id ? "Edit Note" : "Create New Note"}
                                    </h5>
                                    <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                                </div>
                                <div className="modal-body px-4 pb-4">
                                    <input
                                        type="text"
                                        className="form-control border-0 fs-3 fw-bold mb-3 shadow-none p-0 px-1"
                                        placeholder="Note Title"
                                        value={form.title}
                                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                                        autoFocus
                                        style={{ borderBottom: "2px solid #f8f9fa" }}
                                    />
                                    <textarea
                                        className="form-control border-0 shadow-none mb-4 p-1"
                                        rows="12"
                                        placeholder="Start typing your important notes here..."
                                        style={{ resize: "none", fontSize: "1.1rem", lineHeight: "1.6" }}
                                        value={form.content}
                                        onChange={(e) => setForm({ ...form, content: e.target.value })}
                                    />
                                    <div className="row g-4 align-items-end">
                                        <div className="col-6">
                                            <label className="form-label small text-muted">Category</label>
                                            <select className="form-select form-select-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label small text-muted">Priority</label>
                                            <select className="form-select form-select-sm" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                                                {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0 justify-content-between">
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="modalPin"
                                            checked={form.pinned}
                                            onChange={() => setForm({ ...form, pinned: !form.pinned })}
                                        />
                                        <label className="form-check-label small" htmlFor="modalPin">Pin this note</label>
                                    </div>
                                    <button type="submit" className="btn btn-primary px-4 shadow" disabled={saving}>
                                        {saving ? <span className="spinner-border spinner-border-sm" /> : "Save Note"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const NoteCard = ({ note, onEdit, onPin, onDelete, onArchive }) => {
    const priorityColors = {
        high: "danger",
        medium: "warning",
        low: "success"
    };

    return (
        <div className="col-md-4 col-lg-3">
            <div className={`card h-100 shadow-sm border-0 position-relative transition-all note-card ${note.pinned ? 'border-top border-primary border-4' : ''}`}>
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className={`badge bg-light text-dark border small`}>
                            {note.category}
                        </span>
                        <div className="d-flex gap-1">
                            <button className="btn btn-sm p-0 m-0 border-0 shadow-none" onClick={() => onPin(note)}>
                                <i className={`bi ${note.pinned ? 'bi-pin-fill text-primary' : 'bi-pin text-muted'}`}></i>
                            </button>
                        </div>
                    </div>

                    <h5 className="card-title fw-bold" onClick={() => onEdit(note)} style={{ cursor: "pointer" }}>
                        {note.title}
                    </h5>

                    <p className="card-text text-muted small mb-4" onClick={() => onEdit(note)} style={{ cursor: "pointer", whiteSpace: "pre-wrap" }}>
                        {note.content.length > 150 ? note.content.substring(0, 150) + "..." : note.content}
                    </p>

                    <div className="d-flex justify-content-between align-items-center mt-auto">
                        <span className={`small fw-bold text-${priorityColors[note.priority]}`}>
                            {note.priority.toUpperCase()}
                        </span>
                        <div className="d-flex gap-2">
                            <button className="btn btn-sm btn-outline-light text-primary border-0" title="Edit Note" onClick={() => onEdit(note)}>
                                <i className="bi bi-pencil-square"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-light text-muted border-0" title="Archive" onClick={() => onArchive(note)}>
                                <i className={`bi ${note.archived ? 'bi-archive-fill' : 'bi-archive'}`}></i>
                            </button>
                            <button className="btn btn-sm btn-outline-light text-danger border-0" title="Delete Permanent" onClick={() => onDelete(note.id)}>
                                <i className="bi bi-trash"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <style>
                {`
                .note-card:hover { transform: translateY(-3px); box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important; }
                .ls-wide { letter-spacing: 0.1rem; }
                `}
            </style>
        </div>
    );
};
